import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { getAnimationsList } from 'actions/solutionsActions';

const SHARED_VIDEO_KEYS = ['allLocales', '__all', '_all', 'all', 'shared'];

const normalizeVideoFilename = (value) => (
  typeof value === 'string' ? value.trim() : ''
);

const getSharedVideoFilename = (value) => {
  if (typeof value === 'string') return normalizeVideoFilename(value);
  if (!value || typeof value !== 'object') return '';

  for (let index = 0; index < SHARED_VIDEO_KEYS.length; index += 1) {
    const filename = normalizeVideoFilename(value[SHARED_VIDEO_KEYS[index]]);
    if (filename) return filename;
  }

  const uniqueLegacyFilenames = Array.from(new Set(
    Object.keys(value)
      .filter((key) => SHARED_VIDEO_KEYS.indexOf(key) === -1)
      .map((key) => normalizeVideoFilename(value[key]))
      .filter(Boolean)
  ));

  return uniqueLegacyFilenames.length === 1 ? uniqueLegacyFilenames[0] : '';
};

const hasMultipleLegacyValues = (value) => {
  if (!value || typeof value !== 'object') return false;

  const uniqueLegacyFilenames = Array.from(new Set(
    Object.keys(value)
      .filter((key) => SHARED_VIDEO_KEYS.indexOf(key) === -1)
      .map((key) => normalizeVideoFilename(value[key]))
      .filter(Boolean)
  ));

  return uniqueLegacyFilenames.length > 1;
};

const ParentVideosControls = ({
  getAnimationsList,
  value,
  onChange,
  translate,
}) => {
  const [parentVideos, setParentVideos] = useState(null);

  useEffect(() => {
    const getList = async () => {
      const result = await getAnimationsList(['ParentVideos']);
      setParentVideos(result.ParentVideos);
    };

    getList();
  }, [getAnimationsList]);

  const parentVideosOptions = useMemo(() => {
    if (!parentVideos) return [];

    const filenames = [];
    Object.keys(parentVideos).forEach((localeName) => {
      (parentVideos[localeName] || []).forEach((item) => {
        const filename = normalizeVideoFilename(item && item.filename);
        if (filename) filenames.push(filename);
      });
    });

    return Array.from(new Set(filenames)).sort((a, b) => a.localeCompare(b));
  }, [parentVideos]);

  const useSpecific = value !== undefined && value !== null;
  const selectedFilename = getSharedVideoFilename(value);
  const hasLegacyConflict = !selectedFilename && hasMultipleLegacyValues(value);

  return (
    <div style={{ width: 340 }}>
      <div style={{ margin: '2rem 0 1rem' }}>
        <b>Parent Videos:</b>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label>
          <input
            type="radio"
            checked={!useSpecific}
            onChange={() => onChange(undefined)}
          />{' '}
          Use default
        </label>
        <label style={{ marginLeft: 15 }}>
          <input
            type="radio"
            checked={useSpecific}
            onChange={() => onChange({ allLocales: '' })}
          />{' '}
          Use specific
        </label>
      </div>

      {useSpecific && parentVideos && (
        <div style={{ marginBottom: 15 }}>
          <select
            style={{ width: '100%' }}
            value={selectedFilename}
            onChange={(event) => onChange({ allLocales: event.target.value })}
          >
            <option value="">
              {hasLegacyConflict
                ? 'Multiple existing language-specific files - select one shared filename'
                : ''}
            </option>
            {parentVideosOptions.map((filename) => (
              <option key={filename} value={filename}>
                {filename}
              </option>
            ))}
          </select>
          <small style={{ display: 'block', marginTop: 6 }}>
            {translate(
              3774,
              'Enter once; the same filename is used for every language.'
            )}
          </small>
        </div>
      )}
    </div>
  );
};

ParentVideosControls.propTypes = {
  getAnimationsList: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  translate: PropTypes.func.isRequired,
};

const mapStateToProps = ({ status: { translate } }) => ({ translate });

export default connect(mapStateToProps, { getAnimationsList })(
  ParentVideosControls
);
